import { track } from "@amplitude/analytics-react-native";
import { useApolloClient } from "@apollo/client";
import { useCallback, useRef, useState } from "react";
import { Keyboard } from "react-native";
import { useSearchParams } from "solito/navigation";
import { amp } from "src/api/amplitude";
import { PROMPT_SHARE_AFTER_CREATE } from "src/api/featureFlags";
import EditGatheringForCreateDocument from "src/api/graphql/__generated__/documents/EditGatheringForCreateDocument";
import GetDraftGatheringDocument from "src/api/graphql/__generated__/documents/GetDraftGatheringDocument";
import GatheringState from "src/api/graphql/__generated__/enums/GatheringState";
import { useAsyncMutation } from "src/api/graphql/helper";
import { gatheringSelectorHasChangeVar } from "src/api/graphql/variables";
import { BasicModularNavBarTemplate } from "src/components/ModularNavBar";
import { useGatheringContext, useGatheringStore } from "src/contexts/GatheringContext";
import haptic from "src/helpers/haptic";
import log from "src/helpers/log";
import toastError from "src/helpers/toastError";
import useCelebration from "src/hooks/useCelebration";
import useDeleteGathering from "src/hooks/useDeleteGathering";
import useDevice from "src/hooks/useDevice";
import useRouter from "src/hooks/useRouter";
import IconGear from "src/icons/IconGear";
import { alert } from "src/swsh-native";
import usePresentAdminPrivacy from "../../../hooks/usePresentAdminPrivacy";
import useShareGathering from "../../../hooks/useShareGathering";
import useClearSuggestAlbumFromNavigation from "../hooks/useClearSuggestAlbumFromNavigation";
import GatheringHomeAudioPlayerDisplay from "./GatheringHomeAudioPlayerDisplay";

const CreateModeGatheringHomeNavBarContent = () => {
	const router = useRouter();
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const editGatheringMutation = useAsyncMutation(EditGatheringForCreateDocument);
	const [createLoading, setCreateLoading] = useState(false);
	const { handleDeleteGathering: deleteGathering, loading: cancelLoading } = useDeleteGathering();
	const { store } = useGatheringContext();
	const { launchConfettiCannon } = useCelebration();
	const { handleShareGathering } = useShareGathering();
	const searchParams = useSearchParams<{ prev?: string }>();
	const prevRef = useRef<string | undefined>(searchParams?.get("prev"));
	const clearSuggestAlbumFromNavigation = useClearSuggestAlbumFromNavigation();
	const { device } = useDevice();
	const apolloClient = useApolloClient();

	const handleCompleteDraft = useCallback(async () => {
		track(amp.gathering.createButtonPressed);
		const { setEditMode, editGatheringNameCallback } = store.getState();
		try {
			setCreateLoading(true);
			gatheringSelectorHasChangeVar(true);
			haptic("Heavy");
			await editGatheringNameCallback?.("Save").catch(() => {});
			const { data } = await editGatheringMutation({
				variables: {
					input: {
						gatheringId,
						state: GatheringState.Active,
					},
				},
			});
			if (data?.editGathering?.state !== GatheringState.Active) {
				throw new Error("Gathering state not set to Active after creation");
			}
			clearSuggestAlbumFromNavigation();
			setEditMode("None");
			if (PROMPT_SHARE_AFTER_CREATE) {
				handleShareGathering({
					variant: "Create",
				});
			}
			setTimeout(() => {
				launchConfettiCannon("Trifecta");
			}, 200);
		} catch (err) {
			toastError();
			log.error({ err }, "Error creating gathering");
		} finally {
			setCreateLoading(false);
		}
	}, [
		clearSuggestAlbumFromNavigation,
		editGatheringMutation,
		gatheringId,
		handleShareGathering,
		launchConfettiCannon,
		store,
	]);

	const handleCancelDraft = useCallback(() => {
		track(amp.gathering.createCancel.initiate);
		Keyboard.dismiss();
		alert({
			title: "Save your progress?",
			message: "Save your album and finish it up later!",
			webEscapeBehavior: () => {
				track(amp.gathering.createCancel.cancel);
			},
			buttons: [
				{
					text: "Delete",
					style: "destructive",
					onPress: async () => {
						router.prefetch("/home");
						track(amp.gathering.createCancel.delete);
						await deleteGathering({
							gatheringId,
							deleteOwnPhotos: true,
							immediateDelete: false,
						});
						apolloClient.writeQuery({
							query: GetDraftGatheringDocument,
							data: {
								__typename: "Query",
								getDraftGathering: null,
							},
						});

						router.backTo("/home");
					},
				},
				{
					text: "Save",
					style: "default",
					isPreferred: true,
					onPress: () => {
						router.push("/home");
						track(amp.gathering.createCancel.saveDraft);
					},
				},
			],
		});
	}, [apolloClient, deleteGathering, gatheringId, router]);
	const { handlePresentAdminPrivacy } = usePresentAdminPrivacy({
		amplitudeEvent: amp.gathering.editPrivacyButtonPressed,
	});

	return (
		<BasicModularNavBarTemplate
			includeLogo={device === "desktop"}
			items={[
				{
					text: "Cancel",
					onPress: handleCancelDraft,
					testID: "GatheringNavBar-CreateCancel",
					loading: cancelLoading,
				},
				[
					GatheringHomeAudioPlayerDisplay,
					{
						Icon: IconGear,
						onPress: handlePresentAdminPrivacy,
						testID: "GatheringNavBar-EditPrivacy",
					},
					{
						text: "Done",
						onPress: handleCompleteDraft,
						testID: "GatheringNavBar-Create",
						important: true,
						loading: createLoading,
					},
				],
			]}
		/>
	);
};
export default CreateModeGatheringHomeNavBarContent;
